import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, InputAdornment, Switch, TextField } from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ScheduleIcon from "@material-ui/icons/Schedule";
import NumberFormat from "react-number-format";
import { isArray } from "lodash";

import ButtonWithSpinner from "../ButtonWithSpinner";
import useAerosSettingsStyles from "./aerosSettingsStyles";
import { i18n } from "../../translate/i18n";

// Aba "Horários" de /settings, fiel ao mockup Aeros Dark ("Configurações ·
// Horários"). Mesmo contrato do SchedulesForm (initialValues, onSubmit,
// loading) e mesmo formato de dados: 7 objetos { weekday, weekdayEn,
// startTimeA, endTimeA, startTimeB, endTimeB } com horas "HH:mm".
// Mapeamento das colunas: Início=startTimeA, Intervalo=endTimeA,
// Retorno=startTimeB, Fim=endTimeB. "Ativo" é derivado (tem algum horário).

const TABLE_GRID = "130px 60px 1fr 1fr 1fr 1fr";
const TIME_KEYS = ["startTimeA", "endTimeA", "startTimeB", "endTimeB"];
const DEFAULT_TIMES = {
  startTimeA: "08:00",
  endTimeA: "12:00",
  startTimeB: "13:00",
  endTimeB: "18:00",
};
const WEEKDAYS_EN = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const emptySchedules = () =>
  WEEKDAYS_EN.map((weekdayEn) => ({
    weekday: i18n.t(`queueModal.serviceHours.${weekdayEn}`),
    weekdayEn,
    startTimeA: "",
    endTimeA: "",
    startTimeB: "",
    endTimeB: "",
  }));

const isActive = (row) => TIME_KEYS.some((k) => !!row[k]);

const useStyles = makeStyles((theme) => ({
  tableScroll: {
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  head: {
    gridTemplateColumns: TABLE_GRID,
    minWidth: 620,
  },
  row: {
    gridTemplateColumns: TABLE_GRID,
    minWidth: 620,
    padding: "6px 0",
    transition: "opacity 0.15s ease",
  },
  rowInactive: {
    opacity: 0.45,
  },
  dayCell: {
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
  },
  toggleCell: {
    padding: 6,
    display: "flex",
    justifyContent: "center",
  },
  headCenter: {
    textAlign: "center",
  },
  timeCell: {
    padding: "0 6px",
    minWidth: 0,
  },
  // ButtonWithSpinner sobrescreve className (perderia o position:relative do
  // spinner), então o ajuste de padding/fonte do mockup vai pelo wrapper.
  saveButton: {
    "& .MuiButton-root": {
      borderRadius: 10,
      padding: "8px 18px",
      fontSize: 13,
      fontWeight: 700,
    },
  },
  timeInput: {
    "& .MuiOutlinedInput-root": {
      color: theme.palette.text.primary,
    },
    "& .MuiOutlinedInput-adornedEnd": { paddingRight: 10 },
    "& .MuiInputAdornment-root": { marginLeft: 6 },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      fontSize: 14,
      color: theme.palette.aeros.textTertiary,
    },
    "& input::placeholder": {
      color: theme.palette.aeros.textTertiary,
      opacity: 1,
    },
  },
  messageCard: {
    marginTop: 14,
  },
}));

function SchedulesTab({
  initialValues,
  onSubmit,
  loading,
  outOfHoursMessage,
  onOutOfHoursMessageSave,
}) {
  const aeros = useAerosSettingsStyles();
  const classes = useStyles();
  const [schedules, setSchedules] = useState(emptySchedules);
  const [message, setMessage] = useState(outOfHoursMessage || "");

  useEffect(() => {
    setMessage(outOfHoursMessage || "");
  }, [outOfHoursMessage]);

  useEffect(() => {
    if (isArray(initialValues) && initialValues.length > 0) {
      setSchedules(initialValues.map((row) => ({ ...row })));
    }
  }, [initialValues]);

  const updateRow = (index, patch) => {
    setSchedules((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const handleToggle = (index, checked) => {
    if (checked) {
      updateRow(index, { ...DEFAULT_TIMES });
    } else {
      updateRow(index, {
        startTimeA: "",
        endTimeA: "",
        startTimeB: "",
        endTimeB: "",
      });
    }
  };

  // Copia os horários de segunda para terça a sexta (só no estado local).
  const handleCopyWeekdays = () => {
    setSchedules((prev) => {
      const monday = prev.find((r) => r.weekdayEn === "monday") || prev[0];
      if (!monday) return prev;
      const targets = ["tuesday", "wednesday", "thursday", "friday"];
      return prev.map((row) =>
        targets.includes(row.weekdayEn)
          ? {
              ...row,
              startTimeA: monday.startTimeA,
              endTimeA: monday.endTimeA,
              startTimeB: monday.startTimeB,
              endTimeB: monday.endTimeB,
            }
          : row
      );
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(schedules);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={aeros.sectionHead}>
        <div>
          <div className={aeros.sectionTitle}>Horário de expediente</div>
          <div className={aeros.sectionSubtitle}>
            Fora destes horários o contato recebe a mensagem de ausência
          </div>
        </div>
        <div className={aeros.sectionActions}>
          <Button
            type="button"
            variant="text"
            className={aeros.ghostButton}
            startIcon={<FileCopyOutlinedIcon />}
            onClick={handleCopyWeekdays}
          >
            Copiar seg → sex
          </Button>
        </div>
      </div>

      <div className={aeros.tableCard}>
        <div className={classes.tableScroll}>
          <div className={`${aeros.tableHead} ${classes.head}`}>
            <div className={`${aeros.headCell} ${aeros.headCellFirst}`}>Dia</div>
            <div className={`${aeros.headCell} ${classes.headCenter}`}>Ativo</div>
            <div className={aeros.headCell}>Início</div>
            <div className={aeros.headCell}>Intervalo</div>
            <div className={aeros.headCell}>Retorno</div>
            <div className={aeros.headCell}>Fim</div>
          </div>
          {schedules.map((row, index) => {
            const active = isActive(row);
            const label =
              row.weekday || i18n.t(`queueModal.serviceHours.${row.weekdayEn}`);
            return (
              <div
                key={row.weekdayEn || index}
                className={`${aeros.tableRow} ${classes.row} ${
                  active ? "" : classes.rowInactive
                }`}
              >
                <div className={classes.dayCell}>{label}</div>
                <div className={classes.toggleCell}>
                  <Switch
                    className={aeros.toggleSwitchSmall}
                    checked={active}
                    onChange={(e) => handleToggle(index, e.target.checked)}
                    inputProps={{ "aria-label": `${label} ativo` }}
                  />
                </div>
                {TIME_KEYS.map((key) => (
                  <div key={key} className={classes.timeCell}>
                    <NumberFormat
                      customInput={TextField}
                      format="##:##"
                      placeholder="--:--"
                      value={row[key] || ""}
                      disabled={!active}
                      onValueChange={(v) =>
                        updateRow(index, { [key]: v.formattedValue })
                      }
                      variant="outlined"
                      className={`${aeros.inputSmall} ${classes.timeInput}`}
                      inputProps={{ "aria-label": `${label} ${key}` }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <ScheduleIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {onOutOfHoursMessageSave ? (
        <div className={`${aeros.card} ${classes.messageCard}`}>
          <label className={aeros.fieldLabel} htmlFor="out-of-hours-message">
            Mensagem fora do expediente
          </label>
          <TextField
            id="out-of-hours-message"
            variant="outlined"
            multiline
            rows={3}
            fullWidth
            className={aeros.input}
            placeholder="Olá! Nosso atendimento funciona de seg a sex, das 8h às 18h. Retornaremos assim que possível."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onBlur={() => {
              if (message !== (outOfHoursMessage || ""))
                onOutOfHoursMessageSave(message);
            }}
          />
          <div className={aeros.cardDesc}>
            Vazia, vale a mensagem cadastrada na conexão. Aceita {"{{nome}}"} e{" "}
            {"{{ticket}}"}.
          </div>
        </div>
      ) : null}

      <div className={`${aeros.footerActions} ${classes.saveButton}`}>
        <ButtonWithSpinner
          loading={loading}
          type="submit"
          color="primary"
          variant="contained"
        >
          Salvar horários
        </ButtonWithSpinner>
      </div>
    </form>
  );
}

export default SchedulesTab;
